import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Car } from "lucide-react";
import { Link } from "react-router-dom";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import audiLuxury from "@/assets/audi-luxury.jpg";
const Fleet = () => {
  const fleetSchema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Our Fleet",
      "url": "https://ricksblackcarservicesantonio.com/fleet",
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Vehicle",
            "name": "Luxury SUV",
            "vehicleSeatingCapacity": "6",
            "image": "https://ricksblackcarservicesantonio.com/assets/audi-luxury.jpg",
            "description": "Luxury SUV with executive comfort, rear climate control, bottled water, and ample luggage capacity."
          }
        }]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "url": "https://ricksblackcarservicesantonio.com/assets/audi-luxury.jpg",
      "caption": "Rick's Black Car Service 2025 Audi Q7 Premium Luxury SUV",
      "description": "Professional black car service luxury SUV in San Antonio featuring 2025 Audi Q7 with executive package, premium interior, and seating for up to 6 passengers",
      "width": 1920,
      "height": 1080,
      "publisher": {
        "@type": "Organization",
        "name": "Rick's Black Car Service",
        "url": "https://ricksblackcarservicesantonio.com/"
      },
      "copyrightNotice": "© Rick's Black Car Service",
      "creditText": "Rick's Black Car Service"
    }
  ];
  return <>
      <SEO title="Fleet | Luxury SUV | Rick's Black Car San Antonio" description="Explore our luxury SUV—clean, comfortable, photo‑ready. Seats up to 6 with ample luggage space. Book your black car today." canonicalPath="/fleet" schema={fleetSchema} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative py-20 overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center" style={{
            backgroundImage: `url(${audiLuxury})`
          }}>
              <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/60 to-background/80" />
            </div>
            
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <span className="text-primary text-sm font-semibold tracking-wider uppercase">
                  Our Fleet
                </span>
                <h1 className="text-5xl md:text-6xl font-serif font-bold text-foreground">
                  Our Fleet
                </h1>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  Experience luxury and comfort in our meticulously maintained Luxury SUV, perfect for all your transportation needs in San Antonio.
                </p>
              </div>
            </div>
          </section>

          {/* Fleet Details Section */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Card className="card-luxury">
                  <CardContent className="p-10">
                    <div className="text-center mb-8">
                      <Car className="w-16 h-16 text-primary mx-auto mb-6" />
                      <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
                        Luxury SUV
                      </h2>
                      <p className="text-xl text-muted-foreground font-medium mb-2">Seats 1–6 passengers, 4–6 bags</p>
                      <p className="text-lg text-muted-foreground">
                        Executive comfort, rear climate control, bottled water
                      </p>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-2xl font-serif font-bold text-foreground">
                        Features & Amenities
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Executive comfort seating</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Rear climate control</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Complimentary bottled water</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Phone chargers and USB ports</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Child seats on request</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Immaculately maintained</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Photo-ready appearance</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="text-muted-foreground">Ample luggage capacity</span>
                        </div>
                      </div>

                      <div className="mt-8 p-6 bg-secondary/50 rounded-lg">
                        <p className="text-muted-foreground leading-relaxed">
                          Our Luxury SUV is perfect for airport transfers, corporate travel, special events, 
                          and wine tours throughout San Antonio and the Texas Hill Country. With seating for 
                          up to 6 passengers and ample luggage space, our SUV provides the perfect balance 
                          of comfort, style, and practicality.
                        </p>
                      </div>

                      <div className="mt-8 p-6 bg-primary/10 rounded-lg border border-primary/20">
                        <p className="text-lg font-semibold text-foreground mb-3">
                          For Larger Groups
                        </p>
                        <p className="text-muted-foreground">
                          We can schedule multiple SUV trips to accommodate parties larger than 6 passengers. 
                          Contact us to coordinate transportation for your group.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-secondary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
                  Book Your Luxury SUV
                </h2>
                <p className="text-xl text-muted-foreground">
                  Experience the comfort and elegance of our Luxury SUV. Available 24/7 for all your transportation needs.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button className="btn-luxury text-lg px-8 py-6" asChild>
                    <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                      Book 24/7
                    </a>
                  </Button>
                  <Button variant="outline" className="text-lg px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call (210) 296-7409
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Related Services Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground text-center mb-12">
                  Our Services
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">SAT Airport Transfers</h3>
                      <p className="text-sm text-muted-foreground">Flight tracking and meet-and-greet</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/sat-airport-transfers">View SAT Airport Transfers</Link>
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">Corporate Travel</h3>
                      <p className="text-sm text-muted-foreground">Executive transportation solutions</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/services/corporate-travel">View Corporate Travel</Link>
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="card-luxury">
                    <CardContent className="p-6 text-center space-y-3">
                      <h3 className="text-lg font-semibold text-foreground">Wine Tours</h3>
                      <p className="text-sm text-muted-foreground">Hill Country winery experiences</p>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/services/wine-tours">View Wine Tours</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default Fleet;